import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  MoreVertical,
  AlertCircle,
  Maximize2,
  Minimize2
} from 'lucide-react';
import { useGroupStore } from '../store/groupStore';
import { useSocket } from '../contexts/SocketContext';
import { useChatStore } from '../store/chatStore';
import { groupService } from '../services/groupService';
import { LoadingSpinner } from '../components/ui';
import { toast } from '../components/ui/Toaster';
import {
  ConnectionStatus,
  MessageList,
  MessageInput,
  MembersPanel,
  ChatUpgradeCard
} from '../components/chat';
import { EditGroupModal } from '../components/modals';
import { ErrorBoundary } from '../components/ui';
import { isMobile } from '../utils/mobileUtils';

const GroupChatPage: React.FC = () => {
  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();
  const {
    currentGroup,
    groupMembers,
    setCurrentGroup,
    setGroupMembers,
    clearGroupUnread,
  } = useGroupStore();

  const {
    isConnected,
    joinGroup: socketJoinGroup,
  } = useSocket();

  const {
    isLoadingHistory,
    loadChatHistory,
    setCurrentGroup: setActiveChatGroupId,
  } = useChatStore();

  const [showMembers, setShowMembers] = useState(false);
  const [showEditGroup, setShowEditGroup] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Auto-fullscreen for mobile users
  useEffect(() => {
    if (isMobile()) {
      setIsFullscreen(true);
    }
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't intercept if user is typing in an input/textarea
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        // Only handle Escape to close sidebar when typing
        if (e.key === 'Escape' && showMembers) {
          setShowMembers(false);
        }
        return;
      }

      // Tab is deliberately not intercepted: it must keep its default
      // focus-navigation behaviour for keyboard accessibility
      switch (e.key) {
        case 'Escape':
          if (isFullscreen) {
            setIsFullscreen(false);
          } else if (showMembers) {
            setShowMembers(false);
          }
          break;
        case 'm':
        case 'M':
          if (!e.ctrlKey && !e.metaKey) {
            e.preventDefault();
            setShowMembers(!showMembers);
          }
          break;
        case 'f':
        case 'F':
          if (!e.ctrlKey && !e.metaKey) {
            e.preventDefault();
            setIsFullscreen(!isFullscreen);
          }
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showMembers, isFullscreen]);

  const numericGroupId = groupId ? parseInt(groupId) : null;
  const members = numericGroupId ? groupMembers[numericGroupId] || [] : [];
  const isMember = currentGroup?.currentUserMemberStatus === 'ACCEPTED';
  const acceptedCount = members.filter(m => m.status === 'ACCEPTED').length;

  // Load group on mount
  useEffect(() => {
    if (numericGroupId) {
      loadGroup();
    }
  }, [numericGroupId]);

  // Join group chat room when connected and load history. The room is NOT
  // left on unmount: auto-join keeps connected members in their group rooms
  // anyway, and staying in the room is what keeps unread badges live.
  // Joining marks the group as read server-side.
  useEffect(() => {
    if (isConnected && numericGroupId && isMember) {
      socketJoinGroup(numericGroupId);
      loadChatHistory(groupId!);
      setActiveChatGroupId(groupId!);
      clearGroupUnread(numericGroupId);

      return () => {
        setActiveChatGroupId(null);
      };
    }
    // No cleanup needed if not connected/member
    return undefined;
  }, [isConnected, numericGroupId, isMember, socketJoinGroup, loadChatHistory, groupId, setActiveChatGroupId, clearGroupUnread]);


  const loadGroup = async () => {
    if (!numericGroupId) return;

    try {
      // Load group info if not already loaded
      if (!currentGroup || currentGroup.id !== numericGroupId) {
        const group = await groupService.getGroupById(numericGroupId);
        setCurrentGroup(group);
      }

      // Load group members if not already loaded
      if (!groupMembers[numericGroupId]) {
        try {
          const membersData = await groupService.getGroupMembers(numericGroupId);
          setGroupMembers(numericGroupId, membersData.members);
        } catch (memberError: any) {
          // Don't fail the whole page if members can't be loaded
          console.warn('Failed to load group members:', memberError);
        }
      }
    } catch (error: any) {
      console.error('Failed to load group:', error);
      if (error.status === 404) {
        toast.error('Fout', 'Groep niet gevonden');
        navigate('/groups');
      } else if (error.status === 403) {
        toast.error('Toegang geweigerd', 'Je moet lid zijn om toegang te krijgen tot groep chat');
        navigate(`/groups/${numericGroupId}`);
      }
    }
  };


  // Handle group settings click
  const handleGroupSettingsClick = () => {
    setShowEditGroup(true);
  };


  // Handle group update
  const handleGroupUpdated = (updatedGroup: any) => {
    setCurrentGroup(updatedGroup);
  };

  // Show loading if group is not loaded
  if (!currentGroup && !isLoadingHistory) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" text="Groep laden..." />
      </div>
    );
  }

  // Show access denied if not a member
  if (currentGroup && !isMember) {
    return (
      <div className="max-w-2xl mx-auto text-center py-12">
        <AlertCircle className="mx-auto h-12 w-12 text-neon-pink-soft mb-4" />
        <h2 className="text-lg font-bold text-fg-bright mb-2">Toegang geweigerd</h2>
        <p className="text-fg-muted mb-4">
          Je moet lid zijn van deze groep om toegang te krijgen tot de chat.
        </p>
        <Link
          to={`/groups/${groupId}`}
          className="sf-btn-primary px-4 py-2 text-sm"
        >
          Terug naar groep
        </Link>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className={isFullscreen ? 'h-screen fixed inset-0 z-50 bg-canvas' : 'space-y-4'}>
        {/* Main Chat Card */}
        <div className={`flex flex-col bg-surface overflow-hidden ${
          isFullscreen
            ? 'h-full'
            : 'h-[calc(100vh-8rem)] rounded-nav border border-white/[.08] shadow-panel'
        }`}>
          {/* Header */}
          <div
            className="flex items-center justify-between border-b border-white/[.06] px-4 py-3 sm:px-6"
            style={{ background: 'linear-gradient(90deg,rgba(255,61,129,.1),rgba(35,229,219,.06))' }}
          >
            <div className="flex items-center gap-3 min-w-0 flex-1">
              {(!isFullscreen || isMobile()) && (
                <button
                  onClick={() => navigate(`/groups/${groupId}`)}
                  className="p-3 min-h-[44px] min-w-[44px] rounded-xl text-fg-muted hover:text-fg-bright hover:bg-white/[.06] transition-colors flex-shrink-0"
                  title="Terug naar groep"
                >
                  <ArrowLeft className="h-5 w-5" />
                </button>
              )}
              <div className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-xl bg-avatar-pink text-[15px] font-bold text-white">
                {currentGroup?.name?.charAt(0)?.toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <h1 className="text-[17px] font-bold text-fg-bright truncate">
                  {currentGroup?.name}
                </h1>
                <div className="flex items-center gap-2 text-xs">
                  <div className={`flex items-center gap-1.5 ${isConnected ? 'text-neon-lime' : 'text-fg-muted'}`}>
                    {isConnected && <span className="sf-dot-online" />}
                    <span>
                      {isConnected ? 'Verbonden · ' : ''}
                      {acceptedCount} {acceptedCount === 1 ? 'lid' : 'leden'}
                    </span>
                  </div>
                  {!isConnected && <ConnectionStatus />}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-1 text-fg-faint">
              {/* Hide fullscreen toggle on mobile - they auto-get fullscreen */}
              {!isMobile() && (
                <button
                  onClick={() => setIsFullscreen(!isFullscreen)}
                  className="p-3 min-h-[44px] min-w-[44px] rounded-xl text-fg-muted hover:text-fg-bright hover:bg-white/[.06] transition-colors flex-shrink-0"
                  title={isFullscreen ? "Verlaat volledig scherm (F of Esc)" : "Volledig scherm (F)"}
                >
                  {isFullscreen ? (
                    <Minimize2 className="h-5 w-5" />
                  ) : (
                    <Maximize2 className="h-5 w-5" />
                  )}
                </button>
              )}
              <button
                onClick={() => setShowMembers(!showMembers)}
                className="p-3 min-h-[44px] min-w-[44px] rounded-xl text-fg-muted hover:text-fg-bright hover:bg-white/[.06] transition-colors flex-shrink-0"
                title={showMembers ? "Verberg leden (M of Esc)" : "Toon leden (M)"}
              >
                <MoreVertical className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="flex flex-1 overflow-hidden relative">
            {/* Messages */}
            <div
              className="flex-1 flex flex-col min-w-0"
              style={{ background: 'radial-gradient(700px 300px at 100% 0%, rgba(122,61,255,.08), transparent)' }}
            >
              {/* Messages container */}
              <MessageList groupId={groupId!} />

              {/* Message input */}
              <MessageInput groupId={groupId!} />
            </div>

            {/* Members sidebar: full-cover opaque overlay on mobile, static sidebar on sm+ */}
            {showMembers && (
              <div className={`overflow-y-auto absolute inset-0 z-20 bg-surface sm:static sm:z-auto sm:bg-white/[.03] sm:border-l sm:border-white/[.06] ${
                isFullscreen ? 'sm:w-80' : 'sm:w-64'
              }`}>
                <MembersPanel
                  groupId={groupId!}
                  members={members}
                  currentGroup={currentGroup}
                  onGroupSettingsClick={handleGroupSettingsClick}
                  onClose={() => setShowMembers(false)}
                />
              </div>
            )}
          </div>
        </div>

        {/* Chat Upgrade Card - Only show when not in fullscreen */}
        {!isFullscreen && (
          <ChatUpgradeCard groupId={groupId!} />
        )}

        {/* Modals */}
        <EditGroupModal
          isOpen={showEditGroup}
          onClose={() => setShowEditGroup(false)}
          group={currentGroup}
          onGroupUpdated={handleGroupUpdated}
        />
      </div>
    </ErrorBoundary>
  );
};

export default GroupChatPage;